import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import { FirestoreService } from "../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../shared/api/firebase/references";
import { AppDispatch } from "../store/store";

// Provider document from /providers/ collection
export interface SuperAdminProvider {
  uid?: string;
  id: string;
  FirstName?: string;
  LastName?: string;
  EmailAddresses?: string[];
  Status?: string;
  IsAdmin?: boolean;
  FacilityId?: string;
  internal_healthcare_facilities?: string[];
  external_healthcare_facilities?: string[];
  healthcare_facilities?: string[];
  role_id?: string;
}

// Facility info for mapping IDs to names
export interface FacilityInfo {
  id: string;
  name: string;
  isExternal: boolean;
}

export interface SuperAdminProvidersState {
  providers: SuperAdminProvider[];
  facilities: FacilityInfo[];
  loading: boolean;
  error: string | null;
}

const initialState: SuperAdminProvidersState = {
  providers: [],
  facilities: [],
  loading: false,
  error: null,
};

const superAdminProvidersSlice = createSlice({
  name: "superAdminProviders",
  initialState,
  reducers: {
    fetchStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchProvidersSuccess(state, action: PayloadAction<SuperAdminProvider[]>) {
      state.providers = action.payload;
      state.loading = false;
    },
    fetchFacilitiesSuccess(state, action: PayloadAction<FacilityInfo[]>) {
      state.facilities = action.payload;
    },
    fetchFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    resetSuperAdminProviders(state) {
      state.providers = [];
      state.facilities = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  fetchStart,
  fetchProvidersSuccess,
  fetchFacilitiesSuccess,
  fetchFailure,
  resetSuperAdminProviders,
} = superAdminProvidersSlice.actions;

/**
 * Fetch all providers from /providers/ collection
 * Used by SuperAdmin to see all users across all facilities
 */
export const fetchAllProvidersForSuperAdmin =
  () => async (dispatch: AppDispatch) => {
    dispatch(fetchStart());

    try {
      // Fetch all facilities first for name mapping
      const facilities =
        await FirestoreService.getAllDocuments<HealthCareFacilityModel>(
          FirestoreCollectionReference.healthcareFacility(),
        );

      const facilityInfoList: FacilityInfo[] = facilities.map((f) => ({
        id: f.DocId || "",
        name: f.Name || "Unknown Facility",
        isExternal: f.is_external_organization === true,
      }));

      dispatch(fetchFacilitiesSuccess(facilityInfoList));

      const unsubscribe =
        FirestoreService.listenToAllDocuments<SuperAdminProvider>(
          FirestoreCollectionReference.providers(),
          (providersData) => {
            dispatch(fetchProvidersSuccess(providersData));
          },
          (error) => {
            dispatch(fetchFailure(error.message));
          },
        );

      return unsubscribe;
    } catch (error) {
      dispatch(fetchFailure((error as Error).message));
      return () => {};
    }
  };

export default superAdminProvidersSlice.reducer;
